import React from "react";
import { useTranslation } from "utilities/i18n";
import { useDeleteTrialSubscriptionMutation } from "generated/graphql";
import { DefaultModalForm, useModalDispatch } from "collections/Modal/Modal";
import { DISMISS, useNotification } from "collections/Notifications";
import { CancelButton, SubmitButton } from "elements";
import { TrialUser } from "../useTrialUsers";

export const DeleteTrialUserModal: React.FC<{
  refresh: () => void;
  trialUsers: Array<TrialUser>;
}> = ({ trialUsers, refresh }) => {
  const { t } = useTranslation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();
  const [, deleteTrialSubscription] = useDeleteTrialSubscriptionMutation();

  const onSubmit = async () => {
    let success = true;

    const resp = await Promise.all(
      trialUsers.map(async trialUser => {
        const { error } = await deleteTrialSubscription({
          id: trialUser.id
        });
        return { trialUser: trialUser, error: error };
      })
    );

    if (resp.length) {
      resp.forEach(entitlementResp => {
        if (entitlementResp.error) {
          success = false;
          addNotification({
            type: "error",
            dismiss: DISMISS.DEFAULT,
            message: t("ERROR_REMOVING_TRIAL_USER", {
              name: entitlementResp.trialUser.email,
              message: entitlementResp.error
            })
          });
        }
      });

      if (success) {
        closeModal();
        refresh();

        addNotification({
          type: "success",
          dismiss: DISMISS.DEFAULT,
          message: t("SUCCESSFULLY_REMOVED_TRIAL_USERS"),
          persistThroughNav: true
        });
      }
    } else {
      closeModal();
    }
  };

  return (
    <DefaultModalForm
      title={t("REMOVE_TRIAL_USERS", {
        count: trialUsers.length
      })}
      name="DeleteTrialUserModal"
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <>
          <span>
            {t("CONFIRM_REMOVE_TRIAL_USERS", {
              count: trialUsers.length
            })}
          </span>
          <div className="small-text overflow-list modal-body scrollable pt-1 pl-1">
            {trialUsers.map(trialUser => (
              <div key={trialUser.id}>
                <p className="list-item">{trialUser.email}</p>
              </div>
            ))}
          </div>
          <div className="btn-container">
            <CancelButton onClick={closeModal} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="save"
              labelKey="REMOVE_TRIAL_USERS"
              submittingLabelKey="REMOVING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};
